/**
 * 이상한 방 - 화면 아홉이 함께 쓰던 DOM 조립·표시 헬퍼
 *
 * `gameHud` `hud` `mobileControls` `settings` `resultScreen` `levelSelect` `levelIntro`
 * `titleScreen` `juice` - 아홉 파일이 `createElement`를, 넷이 `setText`를, 셋이 "애니메이션
 * 재시작"을, 둘씩이 `setAttr` · `setBarRatio` · `formatNumber` · 다시 칠하기 등록기를
 * 각자 들고 있었다. 몸통은 전부 같았고 주석만 갈려 있었다(같은 규칙을 아홉 번 설명하는
 * 중이었다는 뜻이다). 여기가 원본이다.
 *
 * ## CSS를 끌고 오지 않는다
 *
 * 이 파일은 **스타일시트를 import 하지 않는다.** `formatClock`이 `gameHud.ts`에 있어서 그것을
 * 쓰는 화면은 `gameHud.css`까지 함께 받는데, 여기까지 그러면 화면 하나를 띄우려고 남의 CSS를
 * 두 벌 받게 된다. 공용 파일은 부담 없이 부를 수 있어야 한다(`ui/levelText.ts` 머리말과 같은 이유).
 *
 * ## 여기 두지 않는 것
 *
 * 사본이 하나뿐인 헬퍼는 각 화면에 남겼다 - `hud.ts`의 `setActive`, `gameHud.ts`의 `formatClock`.
 * 쓰는 곳이 하나인 함수를 공용으로 올리면 "여기 있으니 쓰라"가 되어 화면들이 서로의 관용구를
 * 물려받기 시작한다. 두 번째 사본이 생기는 날 올려라 - `toCssColor`(`hud` → `changerBar`)와
 * `setFlag`·`setHidden`(`playerStrip` → `changerBar`·`zoneProgress`)·`setDisabled`(`mobileControls` → `changerBar`)가
 * 그렇게 올라왔다(M4).
 */

/* --- 조립 ---------------------------------------------------------------------- */

/**
 * 클래스 하나를 붙인 엘리먼트. 이 저장소의 DOM은 전부 이 한 줄에서 나온다.
 *
 * `text`를 넘기지 않으면 **건드리지 않는다.** 빈 문자열을 대신 넣지 않는 이유는 값을
 * 나중에 채우는 노드(`show()`가 채운다)와 애초에 글자가 없는 노드(장식·아이콘 조각)를
 * 구별해 두기 위해서다 - 전자는 자리표시 문구를 두지 않는 것이 규칙이다.
 */
export function createElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag)
  element.className = className
  if (text !== undefined) element.textContent = text
  return element
}

/**
 * 텍스트를 바꾸되 **값이 같으면 건드리지 않는다.**
 *
 * `render`가 매 프레임(HUD) · 포인터 이동마다(에디터) 도는 경로라, 이 가드가 곧 프레임
 * 예산이다. 불필요한 `textContent` 쓰기는 그 자리에서 레이아웃 재계산을 부른다.
 */
export function setText(element: HTMLElement, text: string): void {
  if (element.textContent === text) return
  element.textContent = text
}

/**
 * 같은 이유의 속성판. 스크린리더 이름처럼 값에 수치가 섞이는 자리에 쓴다
 * (`aria-label="탄창 2개"`는 탄창을 쓸 때마다 바뀐다).
 */
export function setAttr(element: HTMLElement, name: string, value: string): void {
  if (element.getAttribute(name) === value) return
  element.setAttribute(name, value)
}

/**
 * 게이지 비율의 기록 자릿수. 이보다 잘게 쓰면 매 프레임 style 쓰기가 발생한다.
 * 같은 값이면 DOM을 건드리지 않기 위한 비교 키이기도 하다.
 */
const BAR_PRECISION = 3

/**
 * 게이지 채움. `transform`만 쓰는 이유는 width를 건드리면 레이아웃이 재계산되고,
 * 그건 매 프레임 도는 함수에서 가장 비싼 종류의 쓰기이기 때문이다.
 *
 * 비율은 0~1로 자른다. 게이지를 넘겨 그리면 막대가 칸 밖으로 자라는데, 그건 값이 틀렸다는
 * 신호가 아니라 화면이 깨진 것으로 읽힌다.
 */
export function setBarRatio(fill: HTMLElement, ratio: number): void {
  const clamped = Math.min(Math.max(ratio, 0), 1).toFixed(BAR_PRECISION)
  if (fill.dataset.ratio === clamped) return
  fill.dataset.ratio = clamped
  fill.style.transform = `scaleX(${clamped})`
}

/* --- 토글 ---------------------------------------------------------------------- */

/*
 * 아래 셋은 전부 "값이 같으면 건드리지 않는다"다(`setText`와 같은 이유 - 프레임마다 도는 경로에서 불필요한 DOM 쓰기가
 * 곧 프레임 예산이다). `classList.toggle`·`hidden`·`disabled`는 같은 값을 다시 써도 브라우저가 스타일 무효화를 건너뛴다는
 * 보장이 없어 비교를 우리가 한다.
 */

/** 클래스 토글. */
export function setFlag(element: HTMLElement, className: string, on: boolean): void {
  if (element.classList.contains(className) === on) return
  element.classList.toggle(className, on)
}

/** `hidden` 토글. 각 화면의 CSS가 `[hidden]`을 명시적으로 `display: none`으로 받는다(`playerStrip.css` 등). */
export function setHidden(element: HTMLElement, hidden: boolean): void {
  if (element.hidden === hidden) return
  element.hidden = hidden
}

/** 버튼 잠금. 잠긴 버튼은 제자리에 남는다 - 지우면 손가락이 헛디딘다(`changerBar.ts` 머리말). */
export function setDisabled(button: HTMLButtonElement, disabled: boolean): void {
  if (button.disabled === disabled) return
  button.disabled = disabled
}

/**
 * 같은 애니메이션을 다시 처음부터 돌린다.
 *
 * 클래스를 뗐다 붙이는 것만으로는 재시작되지 않는다 - 브라우저가 두 변경을 한 프레임에
 * 합쳐 보고 "바뀐 것 없음"으로 결론 내린다. 레이아웃 값을 읽으면 그 자리에서 스타일이
 * 플러시되므로 뒤이어 붙이는 클래스가 새 애니메이션으로 인식된다.
 *
 * 강제 리플로우를 일부러 일으키는 코드라 보통은 지워야 할 줄로 읽힌다. 지우지 마라 -
 * 여기서 리플로우는 대가가 아니라 목적이고, 어차피 다음 순간의 애니메이션이 하는 일이다.
 */
export function restartAnimation(element: HTMLElement): void {
  void element.offsetWidth
}

/* --- 표시 서식 ----------------------------------------------------------------- */

/** hex(0xRRGGBB) → CSS 색 문자열에 쓰는 자릿수. */
const HEX_DIGITS = 6
const HEX_RADIX = 16

/** 팔레트 hex(`PaletteColor.hex`) → CSS 색. 색 견본을 그리는 화면(`hud` · `changerBar`)이 같은 변환을 지난다. */
export function toCssColor(hex: number): string {
  return `#${hex.toString(HEX_RADIX).padStart(HEX_DIGITS, '0')}`
}

/**
 * 천 단위 구분.
 *
 * `toLocaleString`을 쓰지 않는다. 실행 환경의 로케일에 따라 같은 점수가 다르게 보이는데,
 * 결정론이 이 프로젝트의 전제라 표시도 흔들리지 않는 편이 낫다. 결과 화면과 선택 화면이
 * 같은 점수를 다른 모양으로 적으면 그 둘이 같은 값이라는 사실 자체가 보이지 않는다.
 */
export function formatNumber(value: number): string {
  const safe = Number.isFinite(value) ? Math.round(value) : 0
  return safe.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/* --- 다시 칠하기 ---------------------------------------------------------------- */

/**
 * 한 번 조립하고 끝나는 글자를 나중에 **다시 칠할 수 있게** 등록한다.
 *
 * 조립 시점에 `t()`를 불러 박아 두면 언어를 바꿨을 때 패널 제목만 옛 언어로 남는다.
 * 사전 엔진은 화면 구조를 모르므로(그게 `setLocale`이 DOM을 안 건드리는 이유다)
 * 다시 그리는 시점은 화면이 알고 있어야 한다.
 */
export type Repaint = (paint: () => void) => void

export type RepaintRegistry = Readonly<{
  /** 다시 칠할 일을 등록한다. **등록 즉시 한 번 칠한다**(아래 참조). */
  repaint: Repaint
  /** 등록된 순서대로 전부 다시 칠한다. `onLocaleChange` 안에서 부른다. */
  repaintAll: () => void
}>

/**
 * 다시 칠할 일들의 목록 하나. 위젯마다 하나씩 만들고 조립하면서 등록한다.
 *
 * **등록이 곧 첫 렌더다.** `repaint(...)`가 넘어온 함수를 그 자리에서 한 번 부르는 이유는
 * "등록"과 "처음 그리기"가 두 벌로 갈라지면 둘 중 하나를 빠뜨린 노드가 생기기 때문이다 -
 * 그 노드는 언어를 바꾸기 전까지 비어 있거나, 언어를 바꿔야 비로소 채워진다.
 */
export function createRepaintRegistry(): RepaintRegistry {
  const repaints: (() => void)[] = []
  return {
    repaint(paint) {
      repaints.push(paint)
      paint()
    },
    repaintAll() {
      for (const paint of repaints) paint()
    },
  }
}
